import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { styleTemplateSampleUrl } from '@/lib/mascotAnimation';
import { getBackendBaseUrl } from '@/services/config/env';

function TemplateSampleImage({ template, className }) {
  const [failed, setFailed] = useState(false);
  const sample = styleTemplateSampleUrl(template, getBackendBaseUrl());
  const label = String(template?.name || 'Style').slice(0, 2).toUpperCase();

  if (!sample || failed) {
    return (
      <div
        className={cn(
          'aspect-square rounded-xl flex flex-col items-center justify-center text-center px-2',
          'bg-gradient-to-br from-sky-100 to-violet-100 dark:from-slate-800 dark:to-slate-700',
          className,
        )}
      >
        <span className="text-lg font-bold text-sky-600 dark:text-sky-300">{label}</span>
        <span className="text-[10px] text-[var(--text-tertiary)] mt-1 capitalize">{template?.category}</span>
      </div>
    );
  }

  return (
    <img
      src={sample}
      alt={`${template.name} sample`}
      className={cn('w-full aspect-square object-cover rounded-xl bg-slate-200 dark:bg-slate-800', className)}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export function MascotStyleTemplatePicker({
  templates = [],
  value,
  onChange,
  disabled = false,
  imageProvider = 'nano_banana',
}) {
  const providerLabel = imageProvider === 'fotor'
    ? 'Fotor img2img'
    : imageProvider === 'inhouse'
      ? 'Inhouse'
      : 'Gemini Nano Banana';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">Choose a Fotor style template</p>
        <p className="text-[10px] text-[var(--text-tertiary)]">
          Converted via {providerLabel}
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange?.('')}
          className={cn(
            'relative rounded-2xl border p-2 text-left transition-all',
            !value
              ? 'border-sky-500 ring-2 ring-sky-500/30 bg-sky-500/10'
              : 'border-[var(--glass-border)] hover:border-sky-300',
            disabled && 'opacity-60 cursor-not-allowed',
          )}
        >
          <div className="aspect-square rounded-xl bg-slate-200/80 dark:bg-slate-800 flex items-center justify-center text-xs text-[var(--text-tertiary)] px-2 text-center">
            Original photo
          </div>
          <p className="mt-2 text-xs font-medium truncate">No style</p>
          {!value ? (
            <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-sky-500 text-white flex items-center justify-center">
              <Check className="w-3 h-3" />
            </span>
          ) : null}
        </button>
        {templates.map((tpl) => {
          const selected = value === tpl.id;
          return (
            <button
              key={tpl.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange?.(tpl.id)}
              className={cn(
                'relative rounded-2xl border p-2 text-left transition-all',
                selected
                  ? 'border-sky-500 ring-2 ring-sky-500/30 bg-sky-500/10'
                  : 'border-[var(--glass-border)] hover:border-sky-300',
                disabled && 'opacity-60 cursor-not-allowed',
              )}
              title={tpl.id}
            >
              <TemplateSampleImage template={tpl} />
              <p className="mt-2 text-xs font-medium truncate">{tpl.name}</p>
              <p className="text-[10px] text-[var(--text-tertiary)] capitalize truncate">{tpl.category}</p>
              {selected ? (
                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-sky-500 text-white flex items-center justify-center">
                  <Check className="w-3 h-3" />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
